import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ProcessExecution {
  _id: string;
  job_key: string;
  job_label?: string;
  project: string;
  trigger: 'cron' | 'manual_console' | 'manual_api' | 'change_stream' | 'internal' | 'retry';
  triggered_by?: any;
  status: 'queued' | 'running' | 'success' | 'partial' | 'failed' | 'cancelled';
  params?: any;
  progress?: { total?: number; processed?: number; ok?: number; failed?: number; skipped?: number; current_batch?: number; eta_ms?: number };
  summary?: any;
  started_at?: string;
  finished_at?: string;
  duration_ms?: number;
  heartbeat_at?: string;
  cancel_requested?: boolean;
  error_message?: string;
  stale?: boolean;
  eta_ms?: number | null;
}

export interface JobDefinition {
  job_key: string;
  label: string;
  category: string;
  project: string;
  manual_trigger_url?: string | null;
  item_columns?: Array<{ key: string; header: string }>;
}

@Injectable({ providedIn: 'root' })
export class ProcessExecutionsService {
  private base = `${environment.BASE_LA}/api/admin/process-executions`;

  constructor(private http: HttpClient) {}

  listJobs() {
    return lastValueFrom(this.http.get<{ ok: boolean; jobs: JobDefinition[] }>(`${this.base}/jobs`));
  }

  list(filters: { job_key?: string; status?: string; project?: string; trigger?: string; from?: string; to?: string; page?: number; limit?: number } = {}) {
    let params = new HttpParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== '') params = params.set(k, String(v)); });
    return lastValueFrom(this.http.get<{ ok: boolean; page: number; limit: number; total: number; items: ProcessExecution[] }>(this.base, { params }));
  }

  get(runId: string) {
    return lastValueFrom(this.http.get<{ ok: boolean; run: ProcessExecution; job_definition: JobDefinition | null }>(`${this.base}/${runId}`));
  }

  progress(runId: string) {
    return lastValueFrom(this.http.get<ProcessExecution & { ok: boolean; stale: boolean }>(`${this.base}/${runId}/progress`));
  }

  events(runId: string, level?: string, page = 1, limit = 100) {
    let params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    if (level) params = params.set('level', level);
    return lastValueFrom(this.http.get<{ ok: boolean; total: number; items: any[] }>(`${this.base}/${runId}/events`, { params }));
  }

  items(runId: string, result?: string, page = 1, limit = 50) {
    let params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    if (result) params = params.set('result', result);
    return lastValueFrom(this.http.get<{ ok: boolean; total: number; items: any[] }>(`${this.base}/${runId}/items`, { params }));
  }

  downloadDetailXlsxUrl(runId: string): string {
    return `${this.base}/${runId}/detail.xlsx`;
  }

  cancel(runId: string) {
    return lastValueFrom(this.http.post<{ ok: boolean; run: ProcessExecution }>(`${this.base}/${runId}/cancel`, {}));
  }

  delete(runId: string) {
    return lastValueFrom(this.http.delete<{ ok: boolean; deleted: { run: number; items: number; events: number } }>(`${this.base}/${runId}`));
  }

  trigger(jobKey: string, body: any = {}) {
    return lastValueFrom(this.http.post<{ ok: boolean; forwarded_to: string; response: any }>(`${this.base}/run/${jobKey}`, body));
  }
}
